import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'results', 'loading'];

  connect() {
    this.resultsVisible = false;
    this.searchTimeout = null;
    this.previousQuery = '';
    this.querySelector = this.resultsTarget.innerHTML;
    this.resultsTarget.innerHTML = '';
    this.setupEventListeners();
  }

  disconnect() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    this.inputTarget.addEventListener('input', this.onInputChange);
    this.inputTarget.addEventListener('focus', this.onFocus);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  removeEventListeners() {
    this.inputTarget.removeEventListener('input', this.onInputChange);
    this.inputTarget.removeEventListener('focus', this.onFocus);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onInputChange = () => {
    const query = this.inputTarget.value.trim();

    // Clear the previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Hide results if query is empty
    if (query === '') {
      this.hideResults();
      return;
    }

    // Skip if query is the same as previous
    if (query === this.previousQuery) {
      return;
    }

    this.previousQuery = query;

    // Set a timeout to avoid making too many requests
    this.searchTimeout = setTimeout(() => {
      this.search(query);
    }, 300);
  };

  onFocus = () => {
    const query = this.inputTarget.value.trim();
    if (query !== '' && this.previousQuery === query) {
      this.showResults();
    }
  };

  onDocumentClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.hideResults();
    }
  };

  onKeydown = (event) => {
    if (!this.resultsVisible) return;

    switch (event.key) {
      case 'Escape':
        this.hideResults();
        event.preventDefault();
        break;
      case 'ArrowDown':
        this.navigateResults(1);
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.navigateResults(-1);
        event.preventDefault();
        break;
      case 'Enter':
        // Let the form handle this
        break;
    }
  };

  navigateResults(direction) {
    const links = this.resultsTarget.querySelectorAll('a');
    if (links.length === 0) return;

    // Find the currently focused element
    let currentIndex = -1;
    links.forEach((link, index) => {
      if (document.activeElement === link) {
        currentIndex = index;
      }
    });

    // Calculate the new index
    let newIndex = currentIndex + direction;
    if (newIndex < 0) {
      newIndex = links.length - 1;
    } else if (newIndex >= links.length) {
      newIndex = 0;
    }

    // Focus the new element
    links[newIndex].focus();
  }

  search(query) {
    // Don't search if query is too short
    if (query.length < 2) {
      this.hideResults();
      return;
    }

    this.showLoading();

    // Modified to only include product in resources type
    const searchUrl = `/search/suggest.json?q=${encodeURIComponent(
      query
    )}&resources[type]=product&resources[options][fields]=title,product_type,variants.title,vendor`;

    fetch(searchUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.renderResults(data, query);
      })
      .catch((error) => {
        console.error('Error fetching search results:', error);
        this.hideLoading();
        this.hideResults();
      });
  }

  renderResults(data, query) {
    this.hideLoading();

    const resources = data.resources.results;
    const products = resources.products || [];

    // If there are no results, hide the results container
    if (products.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="py-4 text-center text-cocoa-500">
          ${window.theme?.translations?.noResultsFound || 'No results found'}
        </div>
      `;
      this.showResults();
      return;
    }

    let resultsHtml = '';

    // Products
    if (products.length > 0) {
      resultsHtml += `<div class="py-2">
        <h3 class="text-heading-xs uppercase mb-2">Products</h3>
        <ul class="space-y-2">
      `;

      products.forEach((product) => {
        const productImage = product.image
          ? `<div class="w-12 h-12 bg-cocoa-100 flex-shrink-0">
            <img src="${product.image}" alt="${product.title}" class="w-full h-full object-cover mix-blend-darken">
          </div>`
          : `<div class="w-12 h-12 bg-cocoa-100 flex-shrink-0"></div>`;

        const price = product.price_max ? `<div class="text-sm">${this.formatMoney(product.price_min)}</div>` : '';

        const vendor = product.vendor ? `<div class="text-xs text-cocoa-500">${product.vendor}</div>` : '';

        resultsHtml += `
          <li>
            <a href="${product.url}" class="flex items-center gap-3 py-2 hover:bg-cocoa-100">
              ${productImage}
              <div class="flex-1 min-w-0">
                ${vendor}
                <div class="text-sm font-medium truncate">${product.title}</div>
                ${price}
              </div>
            </a>
          </li>
        `;
      });

      resultsHtml += `</ul></div>`;
    }

    // Add view all results link
    resultsHtml += `
      <div class="pt-4 pb-2">
        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="button w-full">
          View all products
        </a>
      </div>
    `;

    this.resultsTarget.innerHTML = resultsHtml;
    this.showResults();
  }

  formatMoney(cents) {
    const format = window.theme?.moneyFormat || '€{{amount}}';

    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const value = parseInt(cents || 0) / 100;
    const formattedValue = value.toFixed(2);

    return format.replace('{{amount}}', formattedValue);
  }

  showResults() {
    this.resultsTarget.classList.remove('hidden');
    this.resultsVisible = true;
  }

  hideResults() {
    this.resultsTarget.classList.add('hidden');
    this.resultsVisible = false;
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden');
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden');
    }
  }

  reset() {
    this.inputTarget.value = '';
    this.hideResults();
  }
}
